import axios from 'axios';
import { getAccessToken, clear as clearTokenRefresher, setRefreshUrl as setRefresherUrl } from '@/modules/token-refresher';
import authApi from '@/modules/auth-request';
import { setRefreshToken as setAuthRefreshToken } from '@/modules/auth-request';
import { default as ENV_VARS } from '@/modules/env-vars.js';

const LOGOUT = 'LOGOUT';
const SET_REFRESH_TOKEN = 'SET_REFRESH_TOKEN';
const UPDATING_SESSION = 'UPDATING_SESSION';
const AUTH_SUCCESS = 'AUTH_SUCCESS';
const AUTH_ERROR = 'AUTH_ERROR';
const SET_AUTH_ROOT = 'SET_AUTH_ROOT';
const SET_AUTH_URL = 'SET_AUTH_URL';
const STORE_REFERRER_CLIENT = "STORE_REFERRER_CLIENT";

function decodeToken(token) {
	try {
		/*{
			claims: ["rfo"]
			exp: 1571096175.649
			iat: 1555544175
			iip: "68.47.217.62"
			iss: "https://api.init.st/dev-auth"
			jti: "13ubm91juluu7mp"
			legacyAKID: "D3095029-5A19-4D67-86FA-A2EA011D04F5"
			track: true
			uid: "usr_d30950295a194d6786faa2ea011d04f5"
			usr: "adam@initialstate.com"
			ver: "1"
		}*/
		return (typeof token !== 'string' || token.indexOf('.') < 0) ? null:
			JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
	} catch (err) {
		return null;
	}
}

function getTokenJti(token) {
	const data = decodeToken(state.refreshToken);
	return data && data.jti || '';
}

const state = {

	status: {
		loading: false,
		success: false,
		error: false
	},

	session: null,

	refreshToken: '',
	authBaseUrl: '',
	authRedirectUrl: '',
	referrerClientUrl: ''

};

function getUrl(path) {
	return (state.authBaseUrl || '').replace(/\/$/, '') + '/' + path.replace(/^]\//, '');
}

const mutations = {

	[SET_AUTH_ROOT]: (state, url) => {
		state.authBaseUrl = url || '';
	},

	[SET_AUTH_URL]: (state, url) => {
		state.authRedirectUrl = url || '';
	},

	[UPDATING_SESSION]: (state, isActive) => {
		state.status = {
			loading: isActive === true,
			success: isActive !== true,
			error: false
		};
	},

	[SET_REFRESH_TOKEN]: (state, refreshToken) => {
		if (typeof refreshToken !== 'string') {
			refreshToken = '';
		}

		state.refreshToken = refreshToken;

		if (refreshToken) {
			localStorage.setItem('refreshToken', refreshToken);
		} else {
			localStorage.removeItem('refreshToken');
		}

		clearTokenRefresher();

		setAuthRefreshToken(refreshToken);

		var session = decodeToken(refreshToken);
		state.session = session;
	},

	[AUTH_SUCCESS]: (state, accessToken) => {
		state.status = {
			loading: false,
			success: true,
			error: false
		};
	},

	[AUTH_ERROR]: (state) => {
		state.status = {
			loading: false,
			success: false,
			error: true
		};
	},

	[STORE_REFERRER_CLIENT]: (state, client) => {
		sessionStorage.setItem('referrerClientUrl', client);
		state.referrerClientUrl = client;
	},

	[LOGOUT]: (state) => {
		// clear refresh token and any cached session data
		state.refreshToken = '';
		state.session = null;
		setAuthRefreshToken(null);
		localStorage.removeItem('refreshToken');
	}

};

const actions = {

	[SET_AUTH_ROOT] (context, baseUrl) {
		context.commit(SET_AUTH_ROOT, baseUrl);
		setRefresherUrl(getUrl('tokens'));
	},

	[SET_AUTH_URL] (context, baseUrl) {
		context.commit(SET_AUTH_URL, baseUrl);
	},

	async [SET_REFRESH_TOKEN] (context, refreshToken) {

		// TODO: validate token, get session/claims data

		context.commit(UPDATING_SESSION, true);

		context.commit(SET_REFRESH_TOKEN, refreshToken);

		// Change this to an user update (possible claims change)

		// return axios({url: getUrl('/me'), method: 'GET'})

		return getAccessToken(refreshToken)
			.then((accessToken) => {
				context.commit(AUTH_SUCCESS, accessToken);
				context.commit(UPDATING_SESSION, false);
			})
			.catch((reason) => {
				// clear refresh token
				context.commit(SET_REFRESH_TOKEN, null);
				context.commit(AUTH_ERROR, reason);
				return Promise.reject(reason);
			});
	},

	async [LOGOUT] (context) {
		const refreshJTI = getTokenJti(context.refreshToken);
		return new Promise((resolve) => {
			if (refreshJTI) {
				// Invalidate refresh token
				// TODO: move XHR requests to separate service
				return authApi({url: getUrl('me/security/tokens/' + refreshJTI), method: 'DELETE'})
					.catch(function noop() { /* Ignore failure */ })
					.then(resolve);
			}
			resolve();
		})
			.then(() => {
				context.commit(LOGOUT);
			});
	}

};

const getters = {

	authBaseUrl: state => state.authBaseUrl || '',

	authRedirectUrl: (state) => {
		var url = state.authRedirectUrl || '';
		if (/^https?:\/\/(?:dev-)?account.tekcloud.com$/.test(location.origin)) {
			url = url.replace(/\.initialstate\.com/, '.tekcloud.com');
		}
		return url;
	},

	isAuthenticated: state => {
		const token = decodeToken(state.refreshToken);
		// validate refresh token by expiry
		var expiryMs = (token && token.exp * 1e3 || 0);
		return expiryMs > Date.now();
	},

	userEmail: state => {
		const token = decodeToken(state.refreshToken);
		return token && token.usr || '';
	},

	refreshTokenJTI: state => getTokenJti(state.refreshToken),

	referrerClientUrl: state => state.referrerClientUrl ? (state.referrerClientUrl + '&token='+ state.refreshToken) : null,

	signInUrl: (state, getters, rootState, rootGetters) =>  {
		return ENV_VARS.AUTH_URL + '?client=' + encodeURIComponent((window.location.origin || '') + '#/signin');
	},
	iotAppUrl: (state, getters, rootState, rootGetters) =>  {
		return ENV_VARS.IOT_APP_URL + '?token='+ state.refreshToken;
	},
	appSettingsUrl: (state, getters, rootState, rootGetters) =>  {
		return ENV_VARS.IOT_APP_URL + '?destination=/account&token='+ state.refreshToken;
	},
	appContactsUrl: (state, getters, rootState, rootGetters) =>  {
		return ENV_VARS.IOT_APP_URL + '?destination=/account/contacts&token='+ state.refreshToken;
	},
	/* scope urls */
	scopePrereqUrl: (state) =>  {
		return ENV_VARS.SCOPE_APP_URL + 'prerequisites?token='+ state.refreshToken;
	},
	scopeConfirmationUrl: (state) =>  {
		return ENV_VARS.SCOPE_APP_URL + 'confirmation?token='+ state.refreshToken;
	},
	scopeAddressUrl: (state) =>  {
		return ENV_VARS.SCOPE_APP_URL + 'address?r=existinguser&token='+ state.refreshToken;
	},
	scopeHelpUrl: (state) =>  {
		return ENV_VARS.SCOPE_APP_URL + 'help?token=' + state.refreshToken;
	},
	scopePackagesUrl: (state) =>  {
		return ENV_VARS.SCOPE_PUBLIC_URL + 'pricing';
	},
	updateLicenseUrl: (state) =>  {
		return ENV_VARS.SCOPE_APP_URL + 'license?token=' + state.refreshToken;
	},
	installTekScopeUrl: (state) =>  {
		return ENV_VARS.SCOPE_APP_URL + 'install?token=' + state.refreshToken;
	},
	scopeAppUrl: (state) =>  {
		return ENV_VARS.SCOPE_APP_URL + '?token=' + state.refreshToken;
	},
	/* drive urls */
	driveAppUrl: (state) =>  {
		return ENV_VARS.DRIVE_APP_URL + '?token=' + state.refreshToken;
	},
	drivePlansUrl: (state) => {
		return ENV_VARS.DRIVE_PUBLIC_URL + '#pricing';
	}
};



export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
};



// WEBPACK FOOTER //
// ./src/store/modules/auth.js